import { NgModule } from '@angular/core';
import {
	Routes,
	RouterModule,
	Router,
	Event,
	NavigationEnd,
	NavigationError,
	UrlSegment,
} from '@angular/router';
import {
	AssetResolver,
	CanActivateMyUpdatesRouteGuard,
	CanActivateUpdateRouteGuard,
	CompanyResolver,
	DeploymentContext,
	InitializationGuard,
	JnjErrorHandler,
	ModeResolver,
	PendingChangesGuard,
	RoleCanActivateRouteGuard,
	SearchResultsResolver,
	SeoService,
	TitleAndMetadata,
	UserResolver,
} from '@Common';
import { AcsComponent } from './sso/acs.component.js';
import { CompanyDetailsComponent } from './company-details/company-details.component.js';
import { CompanyUpdateComponent } from './company-update/company-update.component.js';
import { HealthCheckComponent } from './health-check/health-check.component.js';
import { LoginComponent } from './login/login.component.js';
import { MainComponent } from './main/main.component.js';
import { MyUpdatesComponent } from './my-updates/my-updates.component.js';
import { PreferencesComponent } from './user/components/prefereneces/preferences.component.js';
import { SearchResultsComponent } from './search-results/search-results.component.js';
import { SsoComponent } from './sso/sso.component.js';
import { UpdateComponent } from './update-generic/update.component.js';
import {
	CompanyResolutionType,
	LocalizedTextIds,
	RoleGroups,
} from 'company-finder-common';
import { v4 as uuidv4 } from 'uuid';
import { AssetDetailsComponent } from './asset-details/asset-details.component.js';
import { MagicLinkComponent } from './magic-link/magic-link.component.js';

export const routes: Routes = [
	{
		path: '',
		canActivate: [InitializationGuard],
		resolve: { user: UserResolver, mode: ModeResolver },
		runGuardsAndResolvers: 'always',
		children: [
			{
				path: 'details/:id',
				component: CompanyDetailsComponent,
				resolve: { company: CompanyResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: {
					permittedRoleGroup: RoleGroups.AuthenticatedUsers,
					companyResolutionType: CompanyResolutionType.Details,
					supportedModesKey: 'companySpecific',
				},
			},
			// FUTURE - If the company and/or asset-specific routes end up with
			// more repetitive settings than just the supportedModesKey, it may
			// be worth breaking them into new sets of asset and company with childen.
			// Note that this will likely have other changes that need to be implemented,
			// e.g. the parsing of the route data in the Mode Resolver
			{
				path: 'asset/:name',
				component: AssetDetailsComponent,
				resolve: { assetWithCompany: AssetResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: {
					permittedRoleGroup: RoleGroups.AuthenticatedUsers,
					supportedModesKey: 'assetSpecifc',
				},
			},
			{
				// Allows an unqualified route to update a user's company (assumes there will never be a Company with the name "update")
				path: 'company/update', // this route seems necessary to allow an unqualified "company/update" or "company/update/" url to work
				redirectTo: 'company/update/', // this seems to successfully identify company/update/:name rather than company/:name
				data: {
					permittedRoleGroup: RoleGroups.UpdateUsers,
					companyResolutionType: CompanyResolutionType.Update,
					supportedModesKey: 'companySpecific',
				},
			},
			{
				// JUniverse users will land on this unqualified link, which will ensure the JUniverse JWT is processed
				// and then build the qualified route to redirect to.
				path: 'company/update/',
				component: UpdateComponent,
				data: {
					permittedRoleGroup: RoleGroups.UpdateUsers,
					companyResolutionType: CompanyResolutionType.Update,
					supportedModesKey: 'companySpecific',
				},
			},
			{
				path: 'company/update/:name',
				component: CompanyUpdateComponent,
				resolve: { company: CompanyResolver },
				canActivate: [
					RoleCanActivateRouteGuard,
					CanActivateUpdateRouteGuard,
				],
				canDeactivate: [PendingChangesGuard],
				data: {
					permittedRoleGroup: RoleGroups.UpdateUsers,
					companyResolutionType: CompanyResolutionType.Update,
					supportedModesKey: 'companySpecific',
				},
			},
			{
				path: 'company/:name',
				component: CompanyDetailsComponent,
				resolve: { company: CompanyResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: {
					permittedRoleGroup: RoleGroups.AuthenticatedUsers,
					companyResolutionType: CompanyResolutionType.Details,
					supportedModesKey: 'companySpecific',
				},
			},
			{
				path: 'login/debug',
				component: LoginComponent,
				resolve: { searchResults: SearchResultsResolver },
				data: { supportedModesKey: 'companySpecific' },
			},
			{ path: 'login', component: LoginComponent },
			{ path: 'ping', component: SsoComponent },
			{ path: 'acs', component: AcsComponent },
			{ path: 'magic-link', component: MagicLinkComponent },
			{ path: 'health-check', component: HealthCheckComponent },
			// FUTURE - now that there is no concept of public search,
			// it may be worth removing these (J&J is removing all links to these)
			{
				path: 'location/:location', // See ADJQ-187 for more context
				component: SearchResultsComponent,
				resolve: { searchResults: SearchResultsResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: { permittedRoleGroup: RoleGroups.AuthenticatedUsers },
				runGuardsAndResolvers: 'always',
			},
			{
				path: 'filter/:filter', // Similar to locations/ADJQ-187 above
				component: SearchResultsComponent,
				resolve: { searchResults: SearchResultsResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: { permittedRoleGroup: RoleGroups.AuthenticatedUsers },
				runGuardsAndResolvers: 'always',
			},
			{
				path: 'my-updates',
				component: MyUpdatesComponent,
				canActivate: [
					RoleCanActivateRouteGuard,
					CanActivateMyUpdatesRouteGuard,
				],
				data: { permittedRoleGroup: RoleGroups.InternalUsers },
			},
			{
				path: 'search-results',
				component: SearchResultsComponent,
				resolve: { searchResults: SearchResultsResolver },
				canActivate: [RoleCanActivateRouteGuard],
				data: {
					permittedRoleGroup: RoleGroups.AuthenticatedUsers,
					supportedModesKey: 'searchResults',
				},
				runGuardsAndResolvers: 'always',
			},
			{
				path: 'user/preferences',
				component: PreferencesComponent,
				canActivate: [RoleCanActivateRouteGuard],
				data: { permittedRoleGroup: RoleGroups.InternalUsers },
			},
			{
				// For testing ADJQ-1810: Work With jnjinnovation website team to support deeper links
				// There is no harm in including this in a production deployment, and we'd want to be able to test it in Prod as well.
				matcher: (url: UrlSegment[]) =>
					url.join('/').startsWith('test/deep/linking')
						? {
								consumed: url,
								posParams: {
									path: new UrlSegment(
										url.slice(3).join('/'),
										{}
									),
								},
							}
						: null,
				component: HealthCheckComponent, // this is an innoculous component to render for this test
			},
			{
				path: '',
				component: MainComponent,
				data: { supportedModesKey: 'explore' },
			},
			{ path: '**', redirectTo: '/' },
		],
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			onSameUrlNavigation: 'reload',
			bindToComponentInputs: true,
		}),
	],
	exports: [RouterModule],
	providers: [AssetResolver, SearchResultsResolver, UserResolver],
})
export class AppRoutingModule {
	/** Introduced to help debug origination of NavigationErrors if Navigator actually builds a bad one itself
	 * versus deep links in.
	 */
	private previousUrl: string;

	constructor(
		router: Router,
		context: DeploymentContext,
		seoService: SeoService,
		titleAndMetadata: TitleAndMetadata,
		errorHandler: JnjErrorHandler
	) {
		/**
		 * Push route changes up to the parent window.
		 * If the application isn't contained in a parent window (and should be), force it to be.
		 * https://jira.jnj.com/browse/ADJQ-19
		 */
		router.events.subscribe((event: Event) => {
			if (event instanceof NavigationEnd) {
				const url = (<NavigationEnd>event).url;
				this.previousUrl = url;

				// Add a canonical tag to suggest search engines attribute content in this app to the JLABS domain.
				// See https://support.google.com/webmasters/answer/139066?hl=en for more info.
				seoService.addCanonicalTag(url);

				// Set a default title, which might be overidden by individual views in their ngInit, which happens later
				// Although, we still want the Navigator iframed content's default title to be meaningful for web analytics, just don't propagate it to the parent.
				titleAndMetadata.setPageTitle(
					`${context.Loc(LocalizedTextIds.ExplorePageTitle)}`
				);
				titleAndMetadata.upsertMetaTagDescription(
					'Browse the JLABSNavigator to learn more about exciting companies that are driving innovation with JLABS.'
				);

				context.ensureHostedNavigation(url);
				// Don't bother to scroll to top if we're going to scroll elsewhere
				if (!url.startsWith('/search-results')) {
					context.scrollToTopOnNextUpdate = true;
				}
			} else if (event instanceof NavigationError) {
				// The actual NavigationError never makes it to the JnjErrorHandler, so we lose the full URL context.
				// Throwing a new error here with that context will result in JnjErrorHandler trapping 2 errors.
				// Hack: Augment the error, manually invoke the error handler with desired parameters,
				//  and evolve the error handler to ignore the "original" error fingerprint... there really should be a better solution!
				event.error.message += ` when accessing url: ${event.url}, with previousUrl: ${this.previousUrl}`;
				let sendAsWarn = false;

				// ADJQ-2047: Hopefully we've addressed all situations where parenthesis might leak through, messing up routing.
				// Log any occurrences, but just at a warn level.
				if (/[()]/g.test(event.url)) {
					sendAsWarn = true;
					event.error.message =
						'URL contains unencoded parentheses that Angular treats as auxiliary route delimiters (possibly ADJQ-2047). ' +
						event.error.message;
				}

				errorHandler.handleError(
					event.error,
					'Navigation Error',
					true,
					uuidv4(),
					true, // don't toast it to the user
					sendAsWarn
				);
				// ... and just put the user on the landing page
				return router.navigate(['/']);
			}
		});
	}
}
